<template>
    <div id="login-wrapper" :class="['h-screen', 'relative', `${scene}`]">
        <TheHeading
            :style="headerVisible"
            :title="iframeTitle"
            :leftArrow="action !== 'login'"
            @on-back="onBack"
        />
        <Logo v-if="logoVisible" :title="h5Title" />
        <div class="login-container">
            <component
                :is="formComponentKey"
                :action="action"
                :captchaText="captchaText"
                :captchaDisabled="captchaDisabled"
                @on-action="handleAction"
                @on-start="start"
                @on-end="end"
                @on-submit="handleSubmit"
            />
        </div>
        <OAuth v-show="oauthVisible" />
        <div class="agreed-checkout absolute flex">
            <van-checkbox v-model="agreed" />
            <i18n path="login.agreed" tag="div" class="content">
                <a
                    place="privacy"
                    @click="
                        openProtocol(protocol.privacyAgreementUrl, $t('webview.privacyAgreement'))
                    "
                >
                    {{ $t('login.privacyAgreement') }}
                </a>
                <a
                    place="service"
                    @click="
                        openProtocol(
                            protocol.userServiceAgreementUrl,
                            $t('webview.serviceAgreement')
                        )
                    "
                >
                    {{ $t('login.serviceAgreement') }}
                </a>
            </i18n>
        </div>
    </div>
</template>

<script>
/* eslint-disable camelcase */
import { mapMutations, mapState } from 'vuex';
import Cookies from 'js-cookie';
import useEmail from '@/mixins/useEmail';
import { Platform, EmailType } from '@/enums';
import { queryConcat, jsonParser } from '@/lib/utils';
import { getProtocol } from '@/api/login';
import Logs from '@/lib/logs';
import { Action } from './constant';
import Logo from './components/Logo.vue';
import LoginForm from './components/LoginForm.vue';
import RegisterForm from './components/RegisterForm.vue';
import OAuth from './components/OAuth.vue';

export default {
    name: 'Login',
    mixins: [useEmail],
    components: {
        Logo,
        LoginForm,
        RegisterForm,
        OAuth,
    },
    data() {
        return {
            action: Action.Login,
            Action,
            agreed: true, // 默认同意协议
            protocol: {
                privacyAgreementUrl: '',
                userServiceAgreementUrl: '',
            },
            // 埋点日志:
            logs: { web_pageid: 'ghome-web-login' },
        };
    },
    computed: {
        ...mapState({
            platform: state => state.platform || jsonParser(sessionStorage.getItem('platform')),
            returnUrl: state => state.returnUrl,
        }),

        headerVisible() {
            // WebView 单独使用其中一个功能，不需要返回栏
            if (this.platform === Platform.WebView) {
                return 'display: none';
            }
            return '';
        },
        logoVisible() {
            return this.platform === Platform.H5;
        },
        oauthVisible() {
            return this.action === Action.Login;
        },
        scene() {
            return Platform[this.platform];
        },
        h5Title() {
            switch (this.action) {
                case Action.Login:
                    return this.$t('login.LoginByEmail');
                case Action.Register:
                    return this.$t('login.registerByEmail');
                case Action.ResetPassword:
                    return this.$t('login.resetEmailPassword');
                default:
                    return '';
            }
        },
        iframeTitle() {
            if (this.platform !== Platform.Iframe) return '';
            return this.h5Title;
        },
        formComponentKey() {
            return this.action === Action.Login ? 'LoginForm' : 'RegisterForm';
        },
    },
    created() {
        const { appId, return_url = '', action = Action.Login } = this.$route.query;
        if (!appId) this.$toast('No appId parameter');
        if (!this.platform) {
            this.setPlatform(Platform.H5);
            sessionStorage.setItem('platform', JSON.stringify(Platform.H5));
        }
        if (!return_url && this.platform !== Platform.WebView) {
            this.$toast('No return_url parameter');
        }
        const returnUrl = decodeURIComponent(return_url);
        this.setReturnUrl(returnUrl);
        sessionStorage.setItem('returnUrl', JSON.stringify(returnUrl));
        sessionStorage.setItem('emailType', JSON.stringify(action));

        this.action = [EmailType.StaticLogin, EmailType.CodeLogin].includes(action)
            ? Action.Login
            : action;

        // 协议政策
        getProtocol().then(data => {
            Object.assign(this.protocol, data);
        });

        // web日志打点
        Logs.imgLog_PageLoad(this.logs);
    },
    mounted() {
        // 页面加载完成后，webview 需要传递 nav-bar title
        if (this.platform === Platform.WebView) {
            let title = '';
            switch (this.action) {
                case Action.Register:
                    title = this.$t('webview.registerByEmail');
                    break;
                case Action.ResetPassword:
                    title = this.$t('webview.resetEmailPassword');
                    break;
                default:
                    break;
            }
            this.setWebviewTitle(title);
        }
    },
    methods: {
        ...mapMutations(['setReturnUrl', 'setPlatform']),

        onBack() {
            this.action = Action.Login;
            this.end();
        },

        handleAction(action) {
            this.action = action;
            this.end();
        },

        async handleSubmit(emailType, form) {
            // 校验是否同意协议
            if (!this.agreed) {
                return this.$toast(this.$t('login.agreePlaceholder'));
            }

            // 校验是否获取验证码（防止用户乱填）
            if (form.captcha && !this.flowIdList[emailType]) {
                return this.$toast(this.$t('login.obtainCaptcha'));
            }

            const { email } = form;
            switch (emailType) {
                case EmailType.StaticLogin: {
                    const cipherInfo = await this.ueInitClient(email, emailType);
                    await this.emailStaticLogin(
                        {
                            email: cipherInfo,
                            password: form.password,
                        },
                        userInfo => {
                            Logs.imgLog_Button({
                                ...this.logs,
                                web_buttonid: 'static-login',
                                web_userid: `cookie=${Cookies.get('WEB_TOKEN')}`,
                            });
                            this.handleToken(userInfo);
                        }
                    );
                    break;
                }
                case EmailType.CodeLogin:
                    this.emailCaptchaLogin(form).then(userInfo => {
                        Logs.imgLog_Button({
                            ...this.logs,
                            web_buttonid: 'code-login',
                            web_userid: `cookie=${Cookies.get('WEB_TOKEN')}`,
                        });
                        this.handleToken(userInfo);
                    });
                    break;
                case Action.Register:
                    this.emailRegister(form).then(userInfo => {
                        Logs.imgLog_Button({
                            ...this.logs,
                            web_buttonid: 'register',
                            web_userid: `cookie=${Cookies.get('WEB_TOKEN')}`,
                        });
                        this.handleToken(userInfo);
                    });
                    break;
                case Action.ResetPassword:
                    this.emailResetPassword(form).then(userInfo => {
                        Logs.imgLog_Button({
                            ...this.logs,
                            web_buttonid: 'reset-password',
                            web_userid: `cookie=${Cookies.get('WEB_TOKEN')}`,
                        });
                        this.handleToken(userInfo);
                    });
                    break;
                default:
                    break;
            }
        },

        // 针对不同平台，对结果进行不同的处理
        handleToken(userInfo) {
            const { token, ticket, userId, loginType } = userInfo;
            const url = this.handleUrl({ token, ticket, userId, loginType });

            switch (this.platform) {
                case Platform.H5:
                    window.location.href = url;
                    break;
                case Platform.Iframe:
                    window.top.location.href = url;
                    break;
                case Platform.WebView:
                    try {
                        window.AuthCallback({
                            code: 0,
                            data: { token, ticket, userId, loginType },
                            msg: 'SUCCESS',
                        });
                    } catch (error) {
                        console.warn(error);
                    }
                    break;
                default:
                    break;
            }
        },

        // 合并 returnUrl（ returnUrl 可能自带参数）
        handleUrl(query) {
            // prettier-ignore
            const returnUrl = this.returnUrl || jsonParser(sessionStorage.getItem('returnUrl')) || '';
            const parameters = queryConcat(query, /\?/.test(returnUrl) ? '&' : '?', false);
            return returnUrl + parameters;
        },
        openProtocol(src, name) {
            Logs.imgLog_Button({
                ...this.logs,
                web_buttonid: 'protocol',
                web_ext_json: `protocol_type=${name}`,
            });

            if (this.platform === Platform.WebView) {
                this.setWebviewTitle(name);
                this.$router.push({
                    name: 'Protocol',
                    query: { src, name },
                });
            } else {
                window.open(src, '_blank');
            }
        },
        setWebviewTitle(title = '') {
            try {
                window.TitleCallback(title);
            } catch (e) {
                console.error(e);
            }
        },
    },
};
</script>

<style scoped lang="scss">
#login-wrapper {
    min-height: 690px;

    &.iframe {
        min-height: 550px;
    }

    &.webview {
        min-height: 490px;
    }
}

.nav-bar {
    margin-bottom: 20px;
}

.login-container {
    padding: 0 30px;
    overflow: hidden;
}
.webview .login-container {
    padding-top: 20px;
}

/deep/ .submit-btn {
    border-radius: 8px;
}

.agreed-checkout {
    left: 0;
    bottom: 0;
    padding: 0 16px;
    margin-bottom: 20px;
    background-color: transparent !important;
    border-radius: unset !important;
    width: unset;
    color: var(--g-text-gray-color);

    div.van-checkbox {
        width: 35px;
    }

    /deep/.van-checkbox__icon--checked .van-icon {
        background-color: var(--g-primary-color);
        border-color: var(--g-primary-color);
    }

    div.content {
        font-size: 14px;
    }
}
</style>
